import { useEffect, useState } from 'react'
import { ApexOptions } from 'apexcharts'

/**
 * Chart colours that follow the application's light/dark mode (`<html data-bs-theme="...">`).
 * ApexCharts draws its own text, grid and tooltips, so without this they stay light-mode grey on a
 * dark card and become hard to read.
 */
export type ChartTheme = {
  mode: 'light' | 'dark'
  /** headings, legend, tooltip text */
  text: string
  /** axis labels and titles */
  muted: string
  grid: string
  /** tooltip background */
  surface: string
  border: string
  /** heatmap ramp: low / medium / high load */
  heat: { low: string; mid: string; high: string }
  /** text drawn on top of the bright (yellow) bars */
  onBright: string
}

export const LIGHT_THEME: ChartTheme = {
  mode: 'light',
  text: '#181C32',
  muted: '#5E6278',
  grid: '#E4E6EF',
  surface: '#FFFFFF',
  border: '#D9DBE6',
  heat: { low: '#E1F0FF', mid: '#3E97FF', high: '#F1416C' },
  onBright: '#181C32',
}

export const DARK_THEME: ChartTheme = {
  mode: 'dark',
  text: '#F5F8FA',
  muted: '#A1A5B7',
  grid: '#323248',
  surface: '#2B2B40',
  border: '#46465F',
  heat: { low: '#2A3A5C', mid: '#2F80ED', high: '#F1416C' },
  onBright: '#181C32',
}

/** Series colours that read well on both a white and a dark card. */
export const SERIES = {
  green: '#50CD89',
  blue: '#3E97FF',
  yellow: '#FFC700',
  red: '#F1416C',
  purple: '#7239EA',
  cyan: '#009EF7',
}

export function readThemeMode(): 'light' | 'dark' {
  return document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light'
}

/** Re-renders when the user switches the application theme. */
export function useChartTheme(): ChartTheme {
  const [mode, setMode] = useState<'light' | 'dark'>(readThemeMode)
  useEffect(() => {
    const observer = new MutationObserver(() => setMode(readThemeMode()))
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })
    setMode(readThemeMode())
    return () => observer.disconnect()
  }, [])
  return mode === 'dark' ? DARK_THEME : LIGHT_THEME
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v) && typeof v !== 'function'

/**
 * Deep merge where `over` wins and `undefined` never overwrites: ApexCharts crashes when a
 * default setting is replaced by `undefined`.
 */
export function mergeOptions<T extends Record<string, any>>(base: T, over: Record<string, any>): T {
  const out: Record<string, any> = { ...base }
  Object.keys(over).forEach((key) => {
    const value = over[key]
    if (value === undefined) return
    out[key] =
      isPlainObject(value) && isPlainObject(out[key]) ? mergeOptions(out[key], value) : value
  })
  return out as T
}

/** Theme defaults under the chart's own options (the chart's own settings win). */
export function themed(options: ApexOptions, theme: ChartTheme): ApexOptions {
  const base: ApexOptions = {
    chart: { foreColor: theme.muted, background: 'transparent' },
    grid: { borderColor: theme.grid },
    legend: { labels: { colors: theme.text } },
    tooltip: { theme: theme.mode },
    xaxis: {
      axisBorder: { color: theme.grid },
      axisTicks: { color: theme.grid },
      crosshairs: { stroke: { color: theme.muted } },
      // the black x-axis bubble would sit on top of the axis labels; our tooltips already show the date
      tooltip: { enabled: false },
    },
    yaxis: { labels: { style: { colors: theme.muted } } },
    states: { hover: { filter: { type: 'none' } }, active: { filter: { type: 'none' } } },
  }
  return mergeOptions(base as Record<string, any>, options as Record<string, any>) as ApexOptions
}
